// @flow
// ----------------------------------------------------------------------
// <copyright file='Izenda.ReportBodyViewer.js' company='Izenda'>
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * This component is intended to show only report body content.
 * Parameters locate in the params property.
 * id: required. Id of report part.
 *
 * Example build URI:
 * var ContentRoutes = require('routes/Izenda.ContentRoutes').contentRoutes;
 * var ROUTE = require('routes/Izenda.ContentRoutes').route;
 * var uri = routeUtils.getViewerUrl(
 *   'ReportBody',
 *   {
 *     id: 'e395dca2-088a-4ff2-8c54-9412fca495bf',
 *   }
 * );
 * // result: /viewer/reportbody/e395dca2-088a-4ff2-8c54-9412fca495bf?type=pdf
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { forEach, getOr, isNil } from 'lodash/fp';

import connect from 'share/enhancers/Izenda.Connect';
import ReportBodyContent from 'modules/ReportDesigner/Fields/ReportBody/Izenda.ReportBodyContent';
import ReportViewerFilterDescriptions from 'modules/Reports/ReportViewer/Izenda.ReportViewerFilterDescriptions';
import ReportHeader from 'modules/ReportDesigner/Fields/Izenda.ReportHeader';
import ReportFooter from 'modules/ReportDesigner/Fields/Izenda.ReportFooter';
import ReportTitleAndDescription from 'modules/ReportDesigner/Fields/Izenda.ReportTitleAndDescription';

import ReportDesignerStore from 'logic/Izenda.ReportDesignerStore';
import subReportLink from 'share/utils/Izenda.SubReportLink';
import SubReportUtils from 'share/utils/Izenda.SubReportUtils';
import commonMessages from 'share/messages/Izenda.CommonMessages';

import actions from './Izenda.ReportBodyViewerActions';
import PrintingUtils from 'share/utils/Izenda.PrintingUtils';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import classnames from 'classnames';
import PrintConstants from 'logic/data/Izenda.PrintConstants';

@connect(actions)
export default class ReportBodyViewer extends Component {
  static propTypes = {
    params: PropTypes.object
  };

  static defaultProps = {
    hideHeaderFooter: false,
    hideGridPopup: false,
    viewMode: true
  };

  constructor(props: any): void {
    super(props);
    this.init();
  }

  componentWillUnmount() {
    const { props } = this;
    const pageIndex = getOr(0, 'pageIndex', props);
    // If we render several report parts (preview pages) in ExportingContainer: we need to terminate workers only
    // when 1st component is unmounting. Because 2nd and others unmounts when page count is changing and it may
    // cause workers shutdown.
    if (pageIndex > 0)
      return;
    const fieldsStore = this.designerStore.fieldsStore;
    fieldsStore.terminateAllReportPartWorker();
  }

  init() {
    const { props } = this;
    const {
      params, loader, setting, id, viewMode, queryObject, printingMode, confirmFn, localization, skipLoad, partsToRender
    } = props;
    const location = props.location || {};
    const query = location.query || {};
    const defaultVersion = 0;
    const defaultVersionWithSetting = -1;
    let version = query.version || defaultVersion;
    this.renderListeners = [];
    const reportId = id || params.id;
    this.designerStore = props.reportDesignerStore || new ReportDesignerStore(viewMode, reportId);
    const fieldsStore = this.designerStore.fieldsStore;

    setting && !version && (version = defaultVersionWithSetting);

    this.reportHeaderKey = 'ReportHeader';
    this.reportFooterKey = 'ReportFooter';
    PrintingUtils.pushRenderListener(this.renderListeners, this.reportHeaderKey);
    PrintingUtils.pushRenderListener(this.renderListeners, this.reportFooterKey);

    // build render listener
    const buildRenderListeners = () => {
      const renderListeners = this.renderListeners;
      const activeReportParts = partsToRender || fieldsStore.reportBody.activeReportParts;
      forEach(part => {
        renderListeners.push(PrintingUtils.createPartRenderPromise(part.id));
      }, activeReportParts);
    };

    if (skipLoad) {
      fieldsStore.updateReportsParts(false, partsToRender || null);
      return;
    }

    loader.startLoading();

    let promiseResolveQuery = Promise.resolve();

    if (queryObject) {
      // queryObject is filter data that already process, if these filter data in URL we need a step to reprocess data
      this.designerStore.model.setSubReportFilters(queryObject, false);
    } else if (query) {
      const subReportFilterFromQuery = subReportLink.convertQueryToFilter(query);
      this.designerStore.model.setSubReportFilters(subReportFilterFromQuery, true);
      promiseResolveQuery = SubReportUtils.resolveSubReportFilter(subReportFilterFromQuery).then(subReportFilter => {
        this.designerStore.model.setSubReportFilters(subReportFilter, false);
        return null;
      });
    }

    this.designerStore.model.readURLOptions(query);
    promiseResolveQuery
      .then(() => {
        return this.designerStore.loadReportDesigner(version, printingMode && !setting && buildRenderListeners);
      })
      .then(() => {
        if (!printingMode) {
          const subReportFilter = this.designerStore.model.subReportFilter;
          const subReport = {
            selectedReport: reportId,
            masterReportId: subReportFilter.masterReportId,
            reportFilter: subReportFilter.inheritMasterReportFilter
          };
          fieldsStore.validateFilterFieldsTypeSubReport(subReport).then(result => {
            const error = getOr(null, 'messages[0]', result);
            if(!isNil(error)) {
              const commonError = localization.getText('COMMON_MESSAGES_GENERIC_ERROR');
              confirmFn('Error', {
                type: commonMessages.MESSAGE_TYPE.ERROR,
                description: getOr(commonError, 'messages[0]', error),
                showCloseIndicator: true
              });
            } else if (result) {
              confirmFn('Information', {
                type: commonMessages.MESSAGE_TYPE.INFO_ACCEPT,
                description: localization.getText('REPORTDESIGNER_FILTER_LOGIC_SUBREPORT'),
                showCancel: false
              });
            }
          });
        }
        return fieldsStore.getFilterData();
      })
      .then(() => {
        queryObject && fieldsStore.updateSpFilters(queryObject.filters);
        if (setting) {
          loader.endLoading();
          return Promise.resolve(true);
        }
      });
  }

  checkPageBreak() {
    const exportFormatSetting = this.props.exportFormatSetting || this.designerStore.model.export;
    let doesBreakPage = exportFormatSetting.pageBreakAfterReportPart;

    const activeReportParts = this.designerStore.fieldsStore.reportBody.activeReportParts;

    !doesBreakPage &&
    forEach(item => {
      if (item.reportPartContent.checkBreakPage && item.reportPartContent.checkBreakPage()) {
        doesBreakPage = true;
        return false;
      }
    }, activeReportParts);
    return doesBreakPage;
  }

  render() {
    const props = this.props;
    const {
      setting, hideHeaderFooter, hideGridPopup, className, printingMode, getContainerClassName, partsToRender
    } = props;
    const exportFormatSetting = props.exportFormatSetting || this.designerStore.model.export;
    const marginSettingInInches = exportFormatSetting.marginSettingInInches;
    const doesBreakPage = this.checkPageBreak();
    const fieldsStore = this.designerStore.fieldsStore;
    const showReportHeaderAndFooter = !setting && !hideHeaderFooter;

    const headerStyle = {
      height: marginSettingInInches.header,
      overflow: 'hidden'
    };

    const footerStyle = {
      height: marginSettingInInches.footer,
      overflow: 'hidden'
    };

    const containerClass = classnames('content-container', className);

    const content = (
      <div className={containerClass}>
        {showReportHeaderAndFooter && (
          <ReportHeader
            store={fieldsStore}
            viewMode
            replaceSystemVariable={PrintingUtils.replaceSystemVariable}
            style={headerStyle}
            finishedRender={PrintingUtils.getFinishedRenderPromiseById(this.renderListeners, this.reportHeaderKey)}
            printingMode={printingMode}
          />
        )}
        <ReportTitleAndDescription store={fieldsStore} viewMode/>
        <ReportViewerFilterDescriptions store={fieldsStore}/>

        <ReportBodyContent
          store={fieldsStore}
          viewMode
          hideGridPopup={hideGridPopup}
          printingMode={printingMode}
          doesBreakPage={doesBreakPage}
          pageBreakAfterReportPart={exportFormatSetting.pageBreakAfterReportPart}
          exportFormatSetting={exportFormatSetting}
          renderListeners={this.renderListeners}
          setting={setting}
          partsToRender={partsToRender}
        />
        {showReportHeaderAndFooter && (
          <ReportFooter
            store={fieldsStore}
            viewMode
            style={footerStyle}
            finishedRender={PrintingUtils.getFinishedRenderPromiseById(this.renderListeners, this.reportFooterKey)}
            printingMode={printingMode}
          />
        )}
      </div>
    );

    let contentSizeInInches = {};

    if (setting) {
      contentSizeInInches = PrintConstants.getBodyContentSizeInInches(
        exportFormatSetting.pageSizeKey,
        exportFormatSetting.currentMarginSetting,
        exportFormatSetting.currentMarginSetting.header,
        exportFormatSetting.currentMarginSetting.footer,
        exportFormatSetting.orientation
      );
    }

    const printablePageStyle = setting ? { width: '100%', height: '100%' } : {};
    return (
      <section className={getContainerClassName(exportFormatSetting)} style={printablePageStyle}>
        {printingMode ? content : <ScrollBox>{content}</ScrollBox>}
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/views/Izenda.ReportBodyViewer.js